import { useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';

import { toothModel } from '@/entities/tooth';

import {
  MPR_TOGGLE_EVENTS,
  MPR_TOOL_EVENTS,
  type MPRAnalyticsScreen,
  type MPRAnnotationControls,
  type MPRAnnotationTool,
  type MPRCommands,
  type MPRImageControls,
  type MPRImageTool,
  type MPRTool,
} from '../config';

type UseMPRCommandsParams<Tool extends MPRTool> = {
  isAxesVisible: boolean;
  isZoomSynced: boolean;
  screen: MPRAnalyticsScreen;
  tools: readonly Tool[];
  annotations?: MPRAnnotationControls;
  imageOptions?: MPRImageControls;
  isDisabled?: boolean;
  isDownloadDisabled?: boolean;
  toothID?: string;
  onAxesVisibleChange: (isVisible: boolean) => void;
  onToolClick: (tool: Tool) => void;
  onZoomSyncedChange: (isSynced: boolean) => void;
  onDownload?: () => void;
  onStepSlice?: (direction: -1 | 1) => void;
};

export const useMPRCommands = <Tool extends MPRTool>(
  params: UseMPRCommandsParams<Tool>,
): MPRCommands<Tool> => {
  const {
    isAxesVisible,
    isZoomSynced,
    screen,
    tools,
    annotations,
    imageOptions,
    isDisabled = false,
    isDownloadDisabled = false,
    toothID,
    onAxesVisibleChange,
    onToolClick,
    onZoomSyncedChange,
    onDownload,
    onStepSlice,
  } = params;
  const ISONumber = useAppSelector(
    toothModel.selectors.selectISONumberByID(toothID ?? ''),
  );
  const analyticsProperties =
    ISONumber === undefined ? {} : { tooth_number: ISONumber };
  const selectImageTool = (tool: MPRImageTool) => {
    if (isDisabled || !imageOptions?.canEdit) {
      return false;
    }

    if (tool === 'brightnessContrast') {
      trackEvent({
        eventName: MPR_TOOL_EVENTS[screen].contrastBrightnessMode,
        properties: analyticsProperties,
      });
    }
    imageOptions.onToolClick(tool);
    return true;
  };

  return {
    ...(onDownload && {
      download: {
        isDisabled: isDisabled || isDownloadDisabled,
        execute: () => {
          if (!isDisabled && !isDownloadDisabled) {
            onDownload();
          }
        },
      },
    }),
    ...(imageOptions && {
      imageOptions: {
        activeTool: imageOptions.activeTool,
        canEdit: !isDisabled && imageOptions.canEdit,
        selectTool: selectImageTool,
      },
    }),
    ...(annotations && {
      annotations: {
        activeTool: annotations.activeTool,
        canEdit: !isDisabled && annotations.canEdit,
        isVisible: annotations.isVisible,
        selectTool: (tool: MPRAnnotationTool) => {
          if (isDisabled || !annotations.canEdit) {
            return false;
          }

          annotations.onToolClick(tool);
          return true;
        },
        toggleVisibility: () => {
          if (isDisabled) {
            return false;
          }

          annotations.onVisibilityChange(!annotations.isVisible);
          return true;
        },
        cancel: () => !isDisabled && annotations.onCancel(),
        deleteSelected: () =>
          !isDisabled && annotations.canEdit && annotations.onDeleteSelected(),
      },
    }),
    selectTool: (tool) => {
      if (isDisabled || !tools.includes(tool)) {
        return false;
      }

      if (tool === 'contrastBrightnessMode' && imageOptions) {
        return selectImageTool('brightnessContrast');
      }

      trackEvent({
        eventName: MPR_TOOL_EVENTS[screen][tool],
        properties: analyticsProperties,
      });
      onToolClick(tool);
      return true;
    },
    toggleAxes: () => {
      if (isDisabled) {
        return false;
      }

      trackEvent({
        eventName: MPR_TOGGLE_EVENTS[screen].axes,
        properties: {
          ...analyticsProperties,
          tog_status: isAxesVisible ? 'disabled' : 'enabled',
        },
      });
      onAxesVisibleChange(!isAxesVisible);
      return true;
    },
    toggleZoomSync: () => {
      if (isDisabled) {
        return false;
      }

      trackEvent({
        eventName: MPR_TOGGLE_EVENTS[screen].zoomSync,
        properties: {
          ...analyticsProperties,
          tog_status: isZoomSynced ? 'disabled' : 'enabled',
        },
      });
      onZoomSyncedChange(!isZoomSynced);
      return true;
    },
    ...(onStepSlice && {
      stepSlice: (direction: -1 | 1) => {
        if (isDisabled) {
          return false;
        }

        onStepSlice(direction);
        return true;
      },
    }),
  };
};
